/**
 * Operator model-selection vocabulary (IDEA-48 / ADR 0054).
 *
 * WHY here: the provider→model catalog and the operator's `ModelSelection` cross the
 * web↔gateway boundary in both directions (the gateway publishes the catalog, the web
 * sends a selection back), so the vocabulary is a shared, versioned contract. The
 * SERVER owns the actual catalog contents (which models exist, prices, capabilities) —
 * these types only name the shape; nothing here widens what the gateway will accept
 * (validation is fail-closed against the server catalog, ADR 0054).
 */

/**
 * Discrete thinking-depth levels, lowest → highest. Maps 1:1 onto Anthropic's adaptive
 * thinking `effort` parameter — the supported control on current models (the old
 * `budget_tokens` is removed and would be rejected). The UI slider's track is exactly
 * this ordered list, filtered to the chosen model's supported levels.
 */
export const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;
export type EffortLevel = (typeof EFFORT_LEVELS)[number];

/**
 * Providers the catalog can name (IDEA-49 / ADR 0055). Naming a provider here does NOT
 * make it selectable — the gateway's catalog is availability-gated (no server-side key ⇒
 * the provider is absent from `providers` and listed under `disabled` with a reason, and
 * any selection naming it is rejected fail-closed). `custom` is the backend-configured
 * OpenAI-compatible endpoint (`CUSTOM_LLM_*` env). Adding an id stays additive.
 */
export const LLM_PROVIDERS = [
  'anthropic',
  'openai',
  'gemini',
  'deepseek',
  'qwen',
  'kimi',
  'glm',
  'minimax',
  'nemotron',
  'xai',
  'mistral',
  'custom',
] as const;
export type LlmProviderId = (typeof LLM_PROVIDERS)[number];

/**
 * An explicit operator choice of which model runs a generation action. Optional
 * everywhere it appears — absent means "server default" (tier routing, ADR 0047).
 * `thinking` present ⇒ adaptive thinking at that effort level; absent ⇒ the model's
 * default behaviour (note: some models think adaptively by default).
 */
export interface ModelSelection {
  readonly provider: LlmProviderId;
  /** A model id from the server catalog (e.g. `claude-sonnet-5`). Never free-form. */
  readonly model: string;
  readonly thinking?: { readonly level: EffortLevel };
}

/** One selectable model as the server catalog describes it. */
export interface ModelOption {
  readonly provider: LlmProviderId;
  readonly id: string;
  /** Human label for the picker (e.g. "Claude Sonnet 5"). */
  readonly label: string;
  /** Pinned $/1M-token rates — shown in the picker so cost is visible before choosing. */
  readonly inPerMtok: number;
  readonly outPerMtok: number;
  /** Thinking capability: whether the model accepts adaptive thinking, and at which levels. */
  readonly thinking: { readonly supported: boolean; readonly levels: readonly EffortLevel[] };
}

/** One provider's section of the catalog. */
export interface ProviderCatalogEntry {
  readonly id: LlmProviderId;
  readonly label: string;
  readonly models: readonly ModelOption[];
}

/**
 * A provider the server KNOWS but cannot currently call (IDEA-49): its key/endpoint is
 * not configured. Published so diagnostics can show the posture ("openai — OPENAI_API_KEY
 * not set") — the picker ignores these. Never carries secret values, only presence facts.
 */
export interface DisabledProviderEntry {
  readonly id: LlmProviderId;
  readonly label: string;
  /** Human-readable reason, e.g. `OPENAI_API_KEY not set`. */
  readonly reason: string;
}

/** The full server-owned catalog the picker renders (`POST /v1/models/list`). */
export interface ProviderCatalog {
  readonly providers: readonly ProviderCatalogEntry[];
  /** Providers known but unconfigured (diagnostics; optional — absent on older gateways). */
  readonly disabled?: readonly DisabledProviderEntry[];
}
